import { APIErrorResponse } from '../../serviceHelpers/APIErrorResponse';
import JSONResponseHandler from '../../serviceHelpers/JSONResponseHandler';
import SingletonFetch from '../../serviceHelpers/SingletonFetch';
import { isUserProfile, UserProfile } from '../userProfile';
import Identity from '../identity';
import { headers, logPrefix, PROFILE_STORAGE_KEY_EXPIRED_TIME } from '../constants';

/**
 * Gets the logged-in user's profile
 *
 * @returns
 * A __Promise__ resolving the user's profile data. The `userProfile` property will be updated with this data.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *   "firstName": "John",
 *   "lastName": "Doe",
 *   "secondLastName": "Dooey",
 *   "displayName": "john_doe",
 *   "gender": "MALE, FEMALE",
 *   "email": "john.doe@donotreply.com",
 *   "picture": "KEY1",
 *   "birthYear": "2019",
 *   "birthMonth": "12",
 *   "birthDay": "3",
 *   "emailVerified": true,
 *   "contacts": [
 *     {
 *       "phone": "555-555-5555",
 *       "type": "WORK, HOME, PRIMARY, OTHER"
 *     }
 *   ],
 *   "addresses": [
 *     {
 *       "line1": "123 Main St.",
 *       "line2": "string",
 *       "locality": "string",
 *       "region": "string",
 *       "postal": "string",
 *       "country": "string",
 *       "type": "WORK, HOME, PRIMARY, OTHER"
 *     }
 *   ],
 *   "attributes": [
 *     {
 *       "name": "KEY1",
 *       "value": "VALUE 1"
 *     }
 *   ],
 *   "uuid": "string"
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Registration/getProfile
 * ```
 * </details>
 *
 */

export default async function getUserProfile(): Promise<UserProfile | APIErrorResponse> {
  const isLoggedIn = await Identity.isLoggedIn();

  if (!isLoggedIn) {
    throw new Error(`${logPrefix} Missing or invalid jwt`);
  }

  const localUserProfileExpireTime = localStorage.getItem(PROFILE_STORAGE_KEY_EXPIRED_TIME);

  if (
    Identity?.userProfile &&
    localUserProfileExpireTime &&
    new Date(localUserProfileExpireTime) > new Date()
  ) {
    return Identity.userProfile;
  }

  return SingletonFetch(
    `${Identity.apiOrigin}/identity/public/v1/profile`,
    {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
        ...headers
      }
    },
    'getUserProfile'
  )
    .then(JSONResponseHandler)
    .then(json => {
      if (isUserProfile(json)) {
        // Cache the user profile for 10 minute
        const expiredTime = new Date(Date.now() + 10 * 60 * 1000);
        localStorage.setItem(PROFILE_STORAGE_KEY_EXPIRED_TIME, expiredTime.toISOString());

        Identity.userProfile = json;
        return Identity.userProfile;
      } else {
        throw json;
      }
    });
}
